import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { toast } from 'sonner';

import { NotificationToaster } from './Toaster';
import type { NotificationRecord, ToastInput, ToastKind } from './types';
import { NotificationContext } from './useNotifications';

const sonnerByKind: Record<ToastKind, typeof toast.success> = {
  success: toast.success,
  error: toast.error,
  info: toast.info,
  warning: toast.warning,
  loading: toast.loading,
};

const HISTORY_LIMIT = 50;

interface NotificationProviderProps {
  children: ReactNode;
}

// Owns the Sonner Toaster + a running, deduped history of fired notifications.
// Because it sits at the app root, the history survives client-side navigation.
// notify() fires the toast and records it keyed by id (default: a timestamp) —
// passing a stable id lets repeats bump count + lastSeenAt rather than stacking,
// so loops never produce duplicate toasts or duplicate history entries.
export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);

  const notify = useCallback(({ kind = 'info', title, description, action, id, duration }: ToastInput) => {
    const toastId = String(id ?? Date.now());
    sonnerByKind[kind](title, { id: toastId, description, action, duration });

    setNotifications((prev) => {
      const now = Date.now();
      const existing = prev.find((record) => record.id === toastId);
      const updated: NotificationRecord = existing
        ? { ...existing, kind, title, count: existing.count + 1, lastSeenAt: now }
        : { id: toastId, kind, title, count: 1, firstSeenAt: now, lastSeenAt: now };

      return [updated, ...prev.filter((record) => record.id !== toastId)].slice(0, HISTORY_LIMIT);
    });

    return toastId;
  }, []);

  const clear = useCallback(() => setNotifications([]), []);

  const value = useMemo(() => ({ notify, notifications, clear }), [notify, notifications, clear]);

  return (
    <NotificationContext.Provider value={value}>
      {/* Sonner does not replay toasts, so it must subscribe before children's mount effects fire. */}
      <NotificationToaster />
      {children}
    </NotificationContext.Provider>
  );
};
